import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import { captainTcCardService } from "../services/captainTcCardService";
import { toast } from "react-toastify";

function ViewAvailableCaptainTcCards() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [batchRecords, setBatchRecords] = useState({});
  const [loadingRecords, setLoadingRecords] = useState(false);

  // Filters and pagination
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await captainTcCardService.getAvailableBundles();
      if (res.status) {
        setBundles(res.data);
      }
    } catch (error) {
      console.error("Failed to load bundles:", error);
      toast.error("Failed to load available bundles");
    } finally {
      setLoading(false);
    }
  };

  const filteredBundles = bundles.filter((bundle) => {
    const prefixes = bundle.prefixes || "";
    return prefixes.toLowerCase().includes(search.toLowerCase());
  });

  const totalPages = Math.ceil(filteredBundles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBundles = filteredBundles.slice(startIndex, startIndex + itemsPerPage);

  const handleRowClick = async (batchTime) => {
    if (expandedRow === batchTime) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(batchTime);
    if (!batchRecords[batchTime]) {
      setLoadingRecords(true);
      try {
        const res = await captainTcCardService.getCardsByBatch(batchTime);
        if (res.status) {
          setBatchRecords(prev => ({ ...prev, [batchTime]: res.data }));
        }
      } catch (error) {
        toast.error("Failed to load batch records");
      } finally {
        setLoadingRecords(false);
      }
    }
  };

  const downloadCSV = async (batchTime, e) => {
    if (e) e.stopPropagation();
    
    let cardsToDownload = batchRecords[batchTime];
    if (!cardsToDownload) {
      try {
        const res = await captainTcCardService.getCardsByBatch(batchTime);
        if (res.status) {
          cardsToDownload = res.data;
          setBatchRecords(prev => ({ ...prev, [batchTime]: res.data }));
        } else {
          toast.error("Failed to load cards for download");
          return;
        }
      } catch (error) {
        toast.error("Error downloading CSV");
        return;
      }
    }

    if (!cardsToDownload || cardsToDownload.length === 0) {
      toast.info("No cards found in this bundle.");
      return;
    }

    const headers = ["CARD NUMBER", "UNIQUE SERIAL NUMBER"];
    const rows = cardsToDownload.map(c => `${c.card_number},${c.serial_number}`);
    const csvContent = [headers.join(","), ...rows].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const safeName = batchTime.replace(/[\s:]/g, '_');
    link.setAttribute("download", `Bundle_${safeName}_cards.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">View Cards (Bundles)</h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="action-btn" onClick={() => navigate("/manage-captain-tc-cards")} style={{ backgroundColor: '#6b7280' }}>
                Back to Manage Cards
              </button>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by Prefix"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
                maxLength={50}
              />
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>Loading...</div>
          ) : (
            <div className="table-container">
              <table className="member-table">
                <thead>
                  <tr>
                    <th>Batch Time (Generated At)</th>
                    <th>Prefixes</th>
                    <th>Start Card No</th>
                    <th>End Card No</th>
                    <th>Start Serial</th>
                    <th>End Serial</th>
                    <th>Quantity</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBundles.length > 0 ? (
                    paginatedBundles.map((bundle) => (
                      <React.Fragment key={bundle.batch_time}>
                        <tr 
                          onClick={() => handleRowClick(bundle.batch_time)} 
                          style={{ cursor: "pointer", transition: "background-color 0.2s" }} 
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f5f5f5"} 
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                        >
                          <td>{new Date(bundle.batch_time).toLocaleString()}</td>
                          <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={bundle.prefixes}>
                            {bundle.prefixes}
                          </td>
                          <td>{bundle.start_card_no}</td>
                          <td>{bundle.end_card_no}</td>
                          <td>{bundle.start_serial}</td>
                          <td>{bundle.end_serial}</td>
                          <td>{bundle.quantity}</td>
                          <td>
                            <span className="status unassigned">
                              {bundle.status || 'unassigned'}
                            </span>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              className="action-btn"
                              onClick={(e) => downloadCSV(bundle.batch_time, e)}
                              style={{ backgroundColor: '#10b981', padding: "5px 10px", fontSize: "12px", border: "none", borderRadius: "4px", color: "white", cursor: "pointer" }}
                            >
                              Download CSV
                            </button>
                          </td>
                        </tr>
                        {expandedRow === bundle.batch_time && (
                          <tr>
                            <td colSpan="9" style={{ backgroundColor: "#f9f9f9", padding: "15px", borderBottom: "1px solid #eee" }}>
                              {loadingRecords && !batchRecords[bundle.batch_time] ? (
                                <div style={{ textAlign: "center", padding: "10px" }}>Loading records...</div>
                              ) : (
                                <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                                    <thead style={{ backgroundColor: "#eaeaea", position: "sticky", top: 0, zIndex: 1 }}>
                                      <tr>
                                        <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ccc" }}>Card No</th>
                                        <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ccc" }}>Serial Number</th>
                                        <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ccc" }}>Status</th>
                                        <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ccc" }}>Created At</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {batchRecords[bundle.batch_time]?.length > 0 ? (
                                        batchRecords[bundle.batch_time].map((record) => (
                                          <tr key={record.id} style={{ borderBottom: "1px solid #eee" }}>
                                            <td style={{ padding: "8px 10px" }}>{record.card_number}</td>
                                            <td style={{ padding: "8px 10px" }}>{record.serial_number}</td>
                                            <td style={{ padding: "8px 10px" }}>
                                              <span className={`status ${record.status}`} style={{ fontSize: "12px", padding: "2px 6px" }}>
                                                {record.status}
                                              </span>
                                            </td>
                                            <td style={{ padding: "8px 10px" }}>
                                              {new Date(record.created_at).toLocaleString()}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="4" style={{ padding: "10px", textAlign: "center" }}>No records found in this batch.</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: "center" }}>
                        No bundles found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewAvailableCaptainTcCards;
